import {forwardRef, useRef, useState} from "react";
import IconClose from "../../assets/icon-close.svg?react";
import {cn} from "../../utils";
import styles from "./Input.module.css";

export type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "onChange"> & {
    variant?: "default" | "error";
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    clearable?: boolean;
    onClear?: () => void;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
    const {
        className,
        variant = "default",
        startIcon,
        endIcon,
        clearable = false,
        onClear,
        disabled,
        value,
        onChange,
        ...restProps
    } = props;

    const isControlled = value !== undefined;

    const inputRef = useRef<HTMLInputElement | null>(null);
    const [hasValue, setHasValue] = useState(() => {
        const dv = restProps?.defaultValue;
        return dv !== undefined && dv !== null && String(dv).length > 0;
    });

    const isFilled = isControlled
        ? value !== undefined && value !== null && String(value).length > 0
        : hasValue;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isControlled) {
            setHasValue(e.target.value.length > 0);
        }
        onChange?.(e);
    };

    const handleClear = () => {
        const input = inputRef.current;
        if (!input) return;
        if (!disabled) {
            if (isControlled) {
                onClear?.();
            } else {
                input.value = "";
                setHasValue(false);
                onClear?.();
            }
            input.focus();
        }
    };

    const handleWrapperClick = () => {
        inputRef.current?.focus();
    };

    const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
        const target = e.currentTarget;
        if (target.type === "number" && document.activeElement === target) {
            target.blur();
            requestAnimationFrame(() => target.focus());
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const target = e.currentTarget;
        if (target.type === "number" && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
            e.preventDefault();
        }
    };

    const showClearButton = clearable && isFilled && !disabled;

    return (
        <div
            className={cn(
                styles.wrapper,
                styles[variant],
                disabled && styles["disabled"],
                !!startIcon && styles["with-start"],
                showClearButton && styles["with-clear"],
                !!endIcon && styles["with-end"],
                className
            )}
            onClick={handleWrapperClick}
        >
            {startIcon && <span className={styles["start-icon"]}>{startIcon}</span>}
            <input
                ref={(node) => {
                    inputRef.current = node;
                    if (typeof ref === "function") {
                        ref(node);
                    } else if (ref) {
                        ref.current = node;
                    }
                }}
                className={styles.input}
                aria-invalid={variant === "error" ? true : undefined}
                disabled={disabled}
                value={value}
                onChange={handleInputChange}
                onWheel={handleWheel}
                onKeyDown={handleKeyDown}
                {...restProps}
            />
            {showClearButton && (
                <button
                    type="button"
                    className={styles["clear-button"]}
                    onClick={handleClear}
                    tabIndex={-1}
                    aria-label="Clear input"
                >
                    <IconClose />
                </button>
            )}
            {endIcon && <span className={styles["end-icon"]}>{endIcon}</span>}
        </div>
    );
});

Input.displayName = "Input";
